import type { ConversationPlacement } from '@crowdex/agent-contracts/api';
import { rowState, type SidebarWaiting } from '@/lib/desktop-shell/sidebar-model';
import type { SidebarThread } from '@/lib/sidebar-threads';

/** How a task stands now: what the Summary's "Tasks and runs" rows say, live. */
export type LiveTaskState = 'running' | 'waiting' | 'queued' | 'done' | 'failed' | 'stopped';

export type LiveTaskStatus = {
  status: LiveTaskState;
  /** A run's review round inside a loop ("round 2 of 3"). */
  round: { current: number; max: number } | null;
};

/**
 * A task's current state, read as the sidebar reads its row (`rowState`, the same sources: the
 * conversation, what waits on the user, its placement's run), not as it stood when it started:
 * running, waiting on the user, queued, or how it ended. A conversation that is not a workflow's
 * run is done once its agent stopped. Nothing known of it: null (the ref's own state stays).
 */
export function liveTaskStatus(
  id: string,
  thread: SidebarThread | undefined,
  waiting: SidebarWaiting | undefined,
  placement: Pick<ConversationPlacement, 'workflowRun'> | undefined,
): LiveTaskStatus | null {
  const run = placement?.workflowRun ?? null;
  if (thread === undefined && run === null) return null;
  const state = rowState(
    thread ?? { id, title: '', project: '', updatedAt: 0 },
    waiting,
    placement as ConversationPlacement | undefined,
    run !== null,
  );
  const round = run?.round ?? null;
  if (state === 'running') return { status: 'running', round };
  if (state === 'approval' || state === 'question') return { status: 'waiting', round };
  if (run === null) return { status: 'done', round };
  if (run.state === 'succeeded') return { status: 'done', round };
  if (run.state === 'failed') return { status: 'failed', round };
  if (run.state === 'stopped') return { status: 'stopped', round };
  // Begun but at no step yet, or queued for its place.
  return { status: 'queued', round };
}

/** "Running · round 2 of 3", "Done". */
export function liveTaskLabel(status: LiveTaskStatus): string {
  const word = status.status.charAt(0).toUpperCase() + status.status.slice(1);
  return status.round === null || status.status === 'done'
    ? word
    : `${word} · round ${status.round.current} of ${status.round.max}`;
}
